import {Platform} from 'react-native';
import Toast from 'react-native-toast-message';
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
} from 'axios';
import moment from 'moment-timezone';
import {v4 as uuid} from 'uuid';

import config from '../config';
import {AirtableHomepage} from '../models/airtable';
import {
  BillingAddress,
  CreateZenGiftCardPaymentInstrumentRequest,
  TransferTokens,
  User,
} from '../models/mfl';
import {
  Address,
  CreateCompetitionEntryResponse,
  Department,
  EnquireVoucherErrorResponse,
  EnquireVoucherResponse,
  GeocodedSuburb,
  GiftCardRecipientData,
  PostCompetitionEntryFormDataRequest,
  RefreshSessionResponse,
  Shop,
} from '../models/middleware';
import {rootStore} from '../stores/rootStore';
import {dataURItoBlob, getDataUriMediaType} from '../utils/fileUpload';

import {transformUserV2} from './transformers/user.transformer';
import {UserInfoV2Response, UserV2} from './types/user.type';
import {
  jsonApiDeserialisationResponseInterceptor,
  mflAuthHeadersRequestInterceptor,
} from './apiServiceInterceptors';
import {OrderLine} from './apiServiceResponse';
import {
  CommunicationsPreferencesParams,
  MobileVerificationParams,
  OfferCoffeeCardStats,
  RewardsCheckAccountExistsParams,
  RewardsGetAccountParams,
  RewardsGetAccountResponse,
  RewardsInformationResponse,
  RewardsOptInParams,
  RewardsOptOutParams,
  RewardsSignUpParams,
  RewardsSignUpResponse,
  RewardsVerifyExistingPhoneNumberParams,
  RewardsVerifyNewPhoneNumberParams,
  VerifyExistingUserEmailAddressParams,
  ZenOffer,
} from './types';
import { AccountScreenAction } from '../models/navigationTypes';

console.log('>>>>> API Service using:', config.BASE_TENANT, config.BASE_URL);

export const convertAppAddressToMFLAddress = (
  billingAddress: BillingAddress | null | undefined,
) => {
  return {
    lines: billingAddress?.lines?.map((line: any) => line.toUpperCase()),
    postal_code: billingAddress?.postal_code?.toUpperCase(),
    locality: billingAddress?.locality?.toUpperCase(),
    administrative_area: billingAddress?.administrative_area?.toUpperCase(),
  };
};

const convertAppUsertoMFLUser = (values: User) => {
  const birthDate = values.dateOfBirth
    ? moment(values.dateOfBirth)
        .tz('Australia/Sydney')
        .toISOString(true)
        .slice(0, 10)
    : null;

  const billingAddress = convertAppAddressToMFLAddress(values.billingAddress);

  return {
    first_name: values.firstName,
    last_name: values.lastName,
    email_address: values.email,
    birth_date: birthDate,
    phone_number: values.phoneNumber,
    billing_address: billingAddress,
    password: values.password ? values.password : null,
    bypass_compromised_password_validation:
      values.passwordValidationBypass || false,
  };
};

export type CompetitionEntry = Omit<
  PostCompetitionEntryFormDataRequest,
  | 'isOptInLmgMarketing'
  | 'isOptInSupplierMarketing'
  | 'isTermsAccepted'
  | 'isReceiptRequired'
> & {
  isOptInLmgMarketing: boolean;
  isOptInSupplierMarketing: boolean;
  isTermsAccepted: boolean;
};

// TODO: No need for this to be a class, we can just have a functions module
// We are not using OOP in general in this codebase, the TS is mostly for type safety
class ApiService {
  private mwApi!: AxiosInstance;

  constructor() {
    this.initialise(config?.ENV);
  }

  /**
   * Default to prd mfl sandbox. if env: 'DEV' use the mfl dev sandbox. i.e. app development shops
   * MFL_APP_ID_HB vs MFL_APP_ID_HB_DEV
   * @param env
   */
  initialise(env?: 'DEV') {
    this.mwApi = axios.create({
      baseURL: config.BASE_URL,
      headers: {
        Accept: 'application/json, text/plain, */*',
        tenant: config.BASE_TENANT,
        ...(env ? {env} : {}),
      },
    });
    this.mwApi.interceptors.response.use(
      jsonApiDeserialisationResponseInterceptor,
    );
    this.mwApi.interceptors.response.use(
      response => response,
      this.sessionTokenRefreshResponseInterceptor,
    );
    this.mwApi.interceptors.request.use(mflAuthHeadersRequestInterceptor);
  }

  private mflAuthHeadersRequestInterceptor = (
    axiosRequestConfig: AxiosRequestConfig,
  ) => {
    if (
      axiosRequestConfig.headers &&
      (axiosRequestConfig.url?.includes('/ecommerce') ||
        axiosRequestConfig.url?.includes('/competitions') ||
        axiosRequestConfig.url?.includes('/competition-entries'))
    ) {
      // Changes when person logs out of the app
      const deviceId = rootStore?.globalStore?.deviceId as string;
      const sessionSecret = rootStore?.userStore?.sessionSecret as string;
      const sessionToken = rootStore?.userStore?.sessionToken as string;

      axiosRequestConfig.headers.deviceId = deviceId;
      axiosRequestConfig.headers.sessionSecret = sessionSecret;
      axiosRequestConfig.headers.sessionToken = sessionToken;
    }
    return axiosRequestConfig;
  };

  private refreshTokenPromise: Promise<any> | null = null;
  private sessionTokenRefreshResponseInterceptor = async (error: any) => {
    const status = error?.response?.status;
    const originalRequest = error?.config as AxiosRequestConfig & {
      _retry: boolean;
    };

    if (
      originalRequest &&
      originalRequest.url?.includes('/ecommerce') &&
      status === 401 &&
      rootStore.userStore.isLoggedIn()
    ) {
      if (!originalRequest._retry) {
        if (!this.refreshTokenPromise) {
          // Adapted from https://stackoverflow.com/a/60750387/1084192
          console.debug(
            `[SessionRefresh]Access token may have expired (first failing request: ${originalRequest.url}) Refreshing...`,
          );
          const {sessionId, sessionSecret} = rootStore.userStore;

          try {
            this.refreshTokenPromise = this.refreshSession(
              sessionId,
              sessionSecret,
            );

            const sessionInfo = await this.refreshTokenPromise;

            console.debug(
              `[SessionRefresh]Token refresh completed (triggered by ${originalRequest.url})`,
            );

            rootStore.userStore.storeSessionInfo(sessionInfo);
            this.refreshTokenPromise = null;
          } catch (sessionRefreshError) {
            console.debug(
              `[SessionRefresh]Token refresh request failed (triggered by ${originalRequest.url})`,
              sessionRefreshError,
            );
            this.refreshTokenPromise = null;
            return null;
          }
        }

        originalRequest._retry = true;
        try {
          const sessionInfo = await this.refreshTokenPromise;

          if (sessionInfo) {
            console.debug(
              `[SessionRefresh]Unauthorised request (${originalRequest.url}) waiting on existing token refresh request, token refresh completed`,
            );
            return this.mwApi(originalRequest);
          } else {
            console.debug(
              `[SessionRefresh]Unauthorised request (${originalRequest.url}) waiting on existing token refresh, token refresh completed but didn't get refreshed token data. Failing.`,
            );
            return Promise.reject(error);
          }
        } catch (sessionRefreshWaitingError) {
          console.debug(
            `[SessionRefresh]Unauthorised request (${originalRequest.url}) waiting on token refresh, token refresh failed. Failing original request.`,
            sessionRefreshWaitingError,
          );
          return Promise.reject(error);
        }
      } else {
        console.debug(
          "[SessionRefresh]Already retried refreshing token, but didn't work. Showing toast and logging out.",
        );
        Toast.show({
          type: 'errorMessage',
          text1: 'Session expired, please log in again.',
          visibilityTime: 4000,
        });
        rootStore.userStore?.logout();
      }
    }
    return Promise.reject(error);
  };

  private setHeader(headerName: string, headerValue?: string | null) {
    console.debug('[setHeader]', {headerName, headerValue});
    if (headerValue) {
      this.mwApi.defaults.headers.common[headerName] = headerValue;
    } else {
      delete this.mwApi.defaults.headers.common[headerName];
    }
  }

  // Changes when a shop is selected
  setShopId = (shopId: string) => this.setHeader('shopId', shopId);

  // Changes when department is selected
  setDepartment = (departmentId?: object | null) =>
    this.setHeader('department', JSON.stringify(departmentId));

  clearDepartment = () => this.setDepartment(undefined);

  // API stuff
  pingApi = () => {
    return this.mwApi
      .get('/')
      .then(() => {
        console.log('API up', config.BASE_URL);
        return true;
      })
      .catch(() => {
        console.log('API down', config.BASE_URL);
        return false;
      });
  };

  // Competition related
  createCompetitionEntry = async (
    body: CompetitionEntry,
  ): Promise<CreateCompetitionEntryResponse> => {
    const entryId = uuid();

    const formData = new FormData();

    formData.append('id', entryId);

    // copy text values into formdata
    (Object.keys(body) as Array<keyof CompetitionEntry>)
      .filter(key => key !== 'receiptImage')
      .filter(
        key =>
          typeof body[key] !== 'undefined' &&
          body[key] !== 'null' &&
          body[key] !== null,
      )
      .forEach(key => formData.append(key, body[key]));

    // copy receipt image if present
    if (body.receiptImage) {
      let image;
      let filename;
      let filenamePrefix = `competition-entry-id-${entryId}-receipt`;

      // Spec: https://github.com/react-native-image-picker/react-native-image-picker#asset-object
      if (Platform.OS === 'web') {
        // In Web, the URI is a "data" URI.
        // Example: data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAlgC
        const {uri} = body.receiptImage;
        image = dataURItoBlob(uri);
        const dataUriMediaType = getDataUriMediaType(uri);
        const filenameExtension = dataUriMediaType.split('/').pop();
        filename = `${filenamePrefix}.${filenameExtension}`;
      } else {
        // In iOS, the URI is a "file" URI.
        // Example: file:///Users/.../Library/Developer/CoreSimulator/Devices/<GUID>/data/Containers/Data/Application/<GUID>/tmp/<GUID>.jpg
        image = {
          // @ts-ignore
          name: `${filenamePrefix}-${body.receiptImage.uri.split('/').pop()}`,
          type: body.receiptImage.type,
          uri: body.receiptImage.uri,
        };
      }

      // The third argument, 'filename', is only required for Web
      formData.append('receiptImage', image, filename);
    }

    try {
      const response = await this.mwApi.post<CreateCompetitionEntryResponse>(
        config.BASE_URL + '/competitions',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error('unable to create competition entry', err);
      throw err;
    }
  };

  createGameCompetitionEntry =
    async (): Promise<CreateCompetitionEntryResponse> => {
      try {
        const response = await this.mwApi.get<CreateCompetitionEntryResponse>(
          '/competition-entries/summer-cash-grab/entry',
        );
        return response.data;
      } catch (err) {
        console.error('unable to create competition entry', err);
        throw err;
      }
    };

  // SHOP related

  // Only used when in DEV tenant
  getShopsFromMFL = () =>
    this.mwApi
      .get<Shop[]>('/ecommerce/shops')
      // TODO: fix the JSON API deserialisation mess
      .then(res => res as unknown as Shop[])
      .catch(() => {
        console.log('error getting shop list from MFL');
        return [];
      });

  findSalesforceMatch = async (sfId: string) =>
    this.mwApi.get<Shop[]>('/ecommerce/shop-matches', {
      params: {storeNumber: sfId},
    }) as unknown as Shop[]; // TODO: fix the JSON API deserialisation mess

  // findSalesforceMatch = async (sfId: string) => {
  //   try {
  //     const res = await this.mwApi.get<Shop[]>('/ecommerce/shop-matches', {
  //       params: {storeNumber: sfId},
  //     });
  //     // TODO: fix the JSON API deserialisation mess
  //     return res as unknown as Shop[];
  //   } catch (err) {
  //     console.log('error matching shop for id', sfId, err);
  //     return [];
  //   }
  // };

  getLiquorLicense = (sfId: string | null) => {
    return this.mwApi
      .get('/content/license', {params: {storeId: sfId}})
      .then(res => res.data)
      .catch(err => {
        console.log('error loading liquor license', sfId, err);
      });
  };

  getDarksiteId = async (sfId: string) => {
    const res = await this.mwApi.get('/content/darksite', {
      params: {storeId: sfId},
    });
    console.log('found darksite:', res.data);
    return res.data;
  };

  searchSuburbs = async (
    searchText: string,
    cancelToken?: CancelTokenSource,
  ) => {
    try {
      const response = await this.mwApi.get<GeocodedSuburb[]>(
        `/geolocation/suburbs/search/${searchText}`,
        {
          cancelToken: cancelToken?.token,
        },
      );
      return response.data;
    } catch (error: any) {
      if (!axios.isCancel(error)) {
        console.log(
          'Something went wrong cancelling the suburb lookup request:',
          error,
        );
      }
      console.log('[GEOCODE]', error.status, error.body);
      return [];
    }
  };

  reverseGeocode = (lat: number, lon: number) => {
    return this.mwApi
      .get('/geolocation/suburbs/reverse-geocode', {
        params: {
          lat: lat,
          lon: lon,
        },
      })
      .then(response => {
        return response.data as GeocodedSuburb[];
      })
      .catch(error => {
        console.log('[GEOCODE]', error.status, error.body);
        return [] as GeocodedSuburb[];
      });
  };

  addressSearch = async (query: string, {token}: CancelTokenSource) => {
    const response = await this.mwApi.get<Address[]>(
      '/geolocation/addresses/search',
      {
        cancelToken: token,
        params: {query},
      },
    );
    return response.data;
  };

  searchShopBySalesforceId = async (storeNumber: string) => {
    try {
      const res = await this.mwApi.get(`/members/member/${storeNumber}`);
      return res.data;
    } catch (error) {
      console.log('Unable to lookup store', error);
    }
  };

  searchShopsBySuburbPostcodeLatLon = async (
    suburb: string | null,
    postcode: string | undefined,
    lat: number | undefined,
    lon: number | undefined,
    maxDistance: number,
  ) => {
    try {
      const res = await this.mwApi.get<Shop[]>(
        `/members/search/${suburb}/${postcode}/${lat}/${lon}`,
        {
          params: {
            maxDistance,
          },
        },
      );
      return res.data;
    } catch (err) {
      console.log(err);
      return [];
    }
  };

  searchShopsByServiceType = async (
    suburb: string,
    postcode: string,
    serviceType: string,
  ) => {
    try {
      const res = await this.mwApi.get<Shop[]>(
        `/ecommerce/locality-service/${suburb}/${postcode}/${serviceType}`,
      );
      // TODO: fix the JSON API deserialisation mess
      return res as unknown as Shop[];
    } catch (err) {
      console.log(err);
      return [];
    }
  };

  getOpenDeliveryTimes = async () => {
    try {
      const res = await this.mwApi.get('/ecommerce/delivery-times');
      return res;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  getOpenPickupTimes = async () => {
    try {
      const res = await this.mwApi.get('/ecommerce/pickup-times');
      return res;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  //
  // FEED
  //
  getFeedList = (storeNumber: string, storeState: string) => {
    return this.mwApi
      .get('/content/feed', {
        params: {
          storeNumber,
          storeState,
        },
      })
      .then(response => {
        return response.data;
      })
      .catch(err => console.log(err));
  };

  /**
   * Get homepage content
   */
  getHomepage = (storeNumber: string, storeState: string): Promise<AirtableHomepage> => {
    return this.mwApi
      .get('/content/homepage', {
        params: {
          storeNumber,
          storeState,
        },
      })
      .then(({data}) => data)
      .catch(console.log);
  };

  searchByBarcode = (productIDs: string[]) => {
    return this.mwApi
      .get<OrderLine>('/ecommerce/search-productcodes', {
        params: {
          productIDs: productIDs,
        },
      })
      .then(res => res)
      .catch(err => console.log('got error', err));
  };

  //
  // DISCOVER
  //
  getSegments = (storeNumber: string, storeState: string) => {
    return this.mwApi
      .get('/content/discover/segments', {
        params: {
          storeNumber,
          storeState,
        },
      })
      .then(response => {
        return response.data;
      })
      .catch(err => console.log(err));
  };

  //
  // RANGE
  //
  getDepartments = async () =>
    this.mwApi.get('/ecommerce/departments') as unknown as Department[];

  getDepartmentLines = async (params: GetDepartmentLinesRequestParams) =>
    this.mwApi.get('/ecommerce/departments/lines', {
      params,
    });

  getProductById = async (productId: string) => {
    const products = (await this.mwApi.get(
      `/ecommerce/products/${productId}`,
    )) as unknown as OrderLine[];
    return products[0];
  };

  getProductsByDepartmentAndBrand = async (
    brand: {brand: string},
    department: {id: string},
  ) => {
    const customHeader = {
      department: JSON.stringify({department_id: department.id}),
    };
    try {
      const res = await this.mwApi.get('/ecommerce/brand/department/lines', {
        headers: customHeader,
        params: {
          facets: [`brand_name:${brand.brand}`],
        },
      });
      return res;
    } catch (err) {
      console.error('products by departments for brand', err);
      return [];
    }
  };

  getSortOptions = async () => this.mwApi.get('/ecommerce/sort-options');

  getFilterOptions = async () =>
    this.mwApi.get('/ecommerce/department-filters');

  //
  // Search
  //
  searchProductsKeyword = (keyword: string, cancelToken: CancelTokenSource) => {
    return this.mwApi
      .get('/ecommerce/search', {
        cancelToken: cancelToken.token,
        params: {
          keywords: keyword,
        },
      })
      .then(res => res)
      .catch(err => {
        if (!axios.isCancel(err)) {
          console.error('[searchProductsKeyword]', err);
        } else throw err;
      });
  };

  searchPromotedAtCheckout = (searchId: string) => {
    return this.mwApi
      .get('/ecommerce/search', {
        params: {
          embedded_search_id: searchId,
        },
      })
      .then(res => res)
      .catch(err => console.log('[searchPromotedAtCheckout]', err));
  };

  /**
   * Required to fetch lines and sticker data
   * @param params
   * @returns
   */
  searchLines = (params: {
    product_ids?: string[];
    product_codes?: string[];
    order_id?: string;
    member_price?: 'private' | 'public';
  }) => {
    return this.mwApi
      .get('/ecommerce/search', {
        params,
      })
      .then(res => res)
      .catch(err => console.log('[searchLines]', err));
  };

  //
  // Brands
  //
  getBrands = async () => {
    try {
      const response = await this.mwApi.get('/content/brands');
      if (!Array.isArray(response.data))
        throw new Error('Brands response is not an array');
      return response.data as any[];
    } catch (err) {
      console.log(err);
      return [];
    }
  };

  //
  // Activations
  //
  getPromotions = (storeNumber: string, storeState: string) => {
    return this.mwApi
      .get('/content/promotions', {
        params: {
          storeNumber,
          storeState,
        },
      })
      .then(response => response.data)
      .catch(err => console.log(err));
  };

  /**
   * Exclusive Offers - via airtable
   *
   * @param ids
   * @returns
   */
  getOffers = (ids: string[]) => {
    return this.mwApi
      .get('/content/offers', {
        params: {
          ids,
        },
      })
      .then(response => response.data)
      .catch(err => console.log(err));
  };

  applyOffer = async (mflId: string) => {
    return this.mwApi.post(`/ecommerce/order-apply-offer`, {
      mflId,
    });
  };

  // Returns all loyalty deals that are valid for the customer in this shop
  getStoreOffers = async () => {
    return this.mwApi.get('/ecommerce/offers');
  };

  // Returns all coffee card stats for the customer in this shop
  getCoffeeCards = async (storeNumber: string) => {
    const response = await this.mwApi.get<OfferCoffeeCardStats[]>(`/rewards/loyalty/offer-stats`, {
      params: {
        storeNumber,
        type: 'coffee-card',
      },
    });
    return response.data;
  }

  geZenOffers = async () => {
    const response = await this.mwApi.get<ZenOffer[]>(`/rewards/offers`);
    return response.data;
  }

  getZenOffer = async (offerId: string) => {
    const response = await this.mwApi.get<ZenOffer>(`/rewards/offers/${offerId}`);
    return response.data;
  }

  // User Account
  checkEmail = async (email: string) =>
    this.mwApi.get('/ecommerce/email-check', {
      params: {
        email: email,
      },
    });

  checkEmail2 = async (email: string) =>
    this.mwApi.get('/ecommerce/email-check-v2', {
      params: {
        email: email,
      },
    });

  convertLiteUser = async (body: {
    email: string;
    password: string;
    billing_address: any;
    bypass_compromised_password_validation: boolean;
  }) => this.mwApi.post('/ecommerce/convert-lite-user', body);

  verifyEmail = async (
    email: string,
    password: string,
    verificationCode: string,
  ) => {
    return this.mwApi
      .post('/ecommerce/verify-email', {
        email,
        password,
        verification_code: verificationCode,
      })
      .then(res => res)
      .catch(err => {
        console.log('unable to verify email', err);
        return false;
      });
  };

  login = (body: {email: string; password: string}) => {
    return this.mwApi
      .post('/ecommerce/login', body)
      .then(res => res)
      .catch(err => {
        console.log('login error', err);
        throw new Error(err);
        // return err;
      });
  };

  login2 = async (body: {email: string; password: string}) => {
    return this.mwApi
      .post('/ecommerce/login-v2', body)
      .then(res => res)
      .catch(err => {
        console.log('login-v2 error', err);
        throw new Error(err);
      });
  };

  logout = async () => {
    try {
      const res = await this.mwApi.post('/ecommerce/logout');
      return res;
    } catch (err) {
      console.log('logout error', err);
    }
  };

  // API docs: session/how_to_refresh_session_token.html
  refreshSession = async (sessionId: string, sessionSecret: string) => {
    try {
      return this.mwApi.patch<RefreshSessionResponse>(
        '/ecommerce/session-refresh',
        {
          id: sessionId,
          session_secret: sessionSecret,
        },
      ) as unknown as RefreshSessionResponse;
    } catch {
      console.log('session refresh error');
      return null;
    }
  };

  transferSession = async (): Promise<TransferTokens | null> => {
    try {
      // the interceptors mess up the response type
      const response: unknown = await this.mwApi.post<TransferTokens>(
        '/ecommerce/session-transfer',
      );
      return response as TransferTokens;
    } catch (error: any) {
      console.error('session transfer error', error);
      return null;
    }
  };

  getUserInfo = (): any => {
    return this.mwApi
      .get('/ecommerce/user')
      .then(res => res)
      .catch(() => {
        console.log('unable to retrieve user information');
        return null;
      });
  };

  getUserInfoV2 = async (options?: {retryable: boolean}): Promise<UserV2> => this.mwApi
    .get<UserInfoV2Response>('/ecommerce/v2/user', {
      params: {
        ...(options?.retryable ? {retryable: true} : {}),
      },
    })
    .then(res => {
      return transformUserV2(res.data);
    })
    .catch(err => {
      console.error('getUserInfoV2 error', err);
      throw err;
    });

  updateUserInfo = async (
    actionKey: keyof typeof AccountScreenAction,
    user: User, 
    options?: {
      skipConvert?: boolean | undefined, 
      verificationCode?: string,
    }) => {
      const {skipConvert, verificationCode} = options || {};
      const mflUser = skipConvert ? user : convertAppUsertoMFLUser(user);
      const payload = {
        ...mflUser, 
        action: AccountScreenAction[actionKey],
        verification_code: verificationCode,
      }; 
      return await this.mwApi.patch('/ecommerce/user', payload);
  };

  checkPasswordStrength = async (user: User) =>
    this.mwApi.post('/ecommerce/validate-user', convertAppUsertoMFLUser(user));

  sendMobileVerificationCode = async (data: MobileVerificationParams) => {
    try {
      if (data.phoneNumber?.indexOf('0') === 0) {
        data.phoneNumber = '+61' + data.phoneNumber.substring(1);
      }

      // re-using rewards endpoint, could change this to generic mwApi endpoint
      // later for semantic reasons
      return await this.mwApi.post('/rewards/send-verification', {
        data: {
          ...data,
          isEmail: false,
        },
      });
    } catch (err) {
      console.error('sendMobileVerificationCode error', err);
      return null;
    }
  };

  // TODO this will be entirely removed (changed to createUserNew) after
  // all the brands have deployed the rewards/loyalty piece
  createUser = async (user: User) =>
    await this.mwApi.post('/ecommerce/user', convertAppUsertoMFLUser(user));

  createUserNew = async (
    user: User,
    selectedStore: {
      id: string;
      name: string;
    },
    rewardsCheckbox?: boolean | undefined,
    verificationCode?: number | undefined,
  ) => {
    if (!config.FF_LOYALTY_ENABLED) {
      console.error('This should never occur');
    } else {
      return this.mwApi.post('/aggregate/create-account', {
        ...convertAppUsertoMFLUser(user),
        selectedStore,
        rewardsCheckbox,
        verificationCode,
      });
    }
  };

  deleteUser = async (email: string, phoneNumber: string, lastName: string) => {
    try {
      return await this.mwApi.delete(
        `/ecommerce/user?email=${email}&phone=${phoneNumber}&lastName=${lastName}`,
      );
    } catch (err: any) {
      let message = 'Unable to delete user';
      let details = err?.response?.data?.errors?.errors
        ?.map((e: {detail: any}) => e.detail)
        .join(', ');
      if (details) {
        // add a period if needed
        if (details.slice(-1) !== '.') {
          details += '.';
        }
        message = `${message}: ${details} Please contact the helpdesk for support.`;
      }
      throw new Error(message);
    }
  };

  initiatePasswordReset = (values: {email_address: string}) => {
    return this.mwApi
      .post('/ecommerce/user/password', {...values})
      .then(res => res)
      .catch(err => {
        console.log('unable to initiate password reset');
        return err;
      });
  };

  establishSessionWithToken = (token: any) => {
    return this.mwApi
      .post('/ecommerce/session/token', {
        token: token,
      })
      .then(res => res)
      .catch(err => {
        console.log('unable to establish session with reset token');
        throw err;
      });
  };

  getWishlists = () => {
    return this.mwApi
      .get('/ecommerce/wishlist')
      .then(res => res)
      .catch(err => {
        console.log('unable to retrieve wishlists for user');
        return err;
      });
  };

  createWishlist = (name: string) => {
    return this.mwApi
      .post('/ecommerce/wishlist', {shopping_list: {name}})
      .then(res => res)
      .catch(err => {
        console.log('unable to create new wishlist', name);
        return err;
      });
  };

  renameWishlist = (id: any, name: any) => {
    return this.mwApi
      .patch(`/ecommerce/wishlist/${id}`, {shopping_list: {name}})
      .then(res => res)
      .catch(err => {
        console.log('unable to rename wishlist', id, name);
        return err;
      });
  };

  deleteWishlist = (id: any) => {
    return this.mwApi
      .delete(`/ecommerce/wishlist/${id}`)
      .then(res => res)
      .catch(err => {
        console.log('unable to delete wishlist', id);
        return err;
      });
  };

  addItemToWishlist = (wishlistId: any, productId: any, barcode: any) => {
    return this.mwApi
      .post(`/ecommerce/wishlist/${wishlistId}/contents/`, {
        productId: productId,
        barcode: barcode,
      })
      .then(res => res)
      .catch(err => {
        console.log(
          'unable to add item to wishlist',
          wishlistId,
          productId,
          barcode,
        );
        return err;
      });
  };

  getItemsInWishlist = (id: any) => {
    return this.mwApi
      .get(`/ecommerce/wishlist/${id}/contents`)
      .then(res => res)
      .catch(err => {
        console.log('unable to retrieve wishlist items', id);
        return err;
      });
  };

  removeItemFromWishlist = (wishlistId: any, entryId: any, barcode: any) => {
    return this.mwApi
      .delete(`/ecommerce/wishlist/${wishlistId}/contents/${entryId}`, {
        data: {barcode: barcode},
      })
      .then(res => res)
      .catch(err => {
        console.log(
          'unable to remove item from wishlist',
          wishlistId,
          entryId,
          barcode,
        );
        return err;
      });
  };

  // Card tokenisation
  getAvailablePaymentServices = () =>
    this.mwApi.get('/ecommerce/payment-services');

  getTokenizationData = (serviceId: any) => {
    return this.mwApi
      .post(`/ecommerce/payment-services/${serviceId}`)
      .then(res => res)
      .catch(err => {
        console.log('unable to retrieve tokenization data for service', err);
      });
  };

  // Payment methods
  getAvailableInstruments = () => {
    return this.mwApi
      .get('/ecommerce/payment-instruments')
      .then(res => res)
      .catch(err => {
        console.log('unable to retrieve payment instruments', err);
      });
  };

  createPaymentInstrument = (serviceId: any, tillTransactionToken: any) => {
    return this.mwApi
      .post('/ecommerce/payment-instruments', {
        serviceId,
        tillTransactionToken,
        challenge: false, // LLP-662 3DS not required for adding new payment option 
      })
      .then(res => res)
      .catch(err => {
        console.log(
          'unable to create payment instrument',
          err.response?.status,
          err.response?.data,
        );
        throw new Error(err.response?.data || 'Unable to store card');
      });
  };

  // Continue here....
  // createZenGiftCardPaymentInstrument = async (giftCardNumber: string, pin: string) => this.mwApi

  deletePaymentInstrument = (instrumentId: any) => {
    return this.mwApi
      .delete('/ecommerce/payment-instruments', {
        data: {instrumentId},
      })
      .then(res => res)
      .catch(() => {
        console.log('unable to delete payment instrument');
        throw new Error('Unable to delete credit card at this time');
      });
  };

  // Orders
  loadCurrentOrder = () => {
    return this.mwApi
      .get('/ecommerce/order')
      .then(res => res.data[0])
      .catch(err => console.log('unable to load current order', err));
  };

  loadRecentOrders = async () => {
    const res = await this.mwApi.get('/ecommerce/order/recent');
    return res.data;
  };

  changeOrderLines = (lines: {product_id: string; quantity: number}[]) => {
    return this.mwApi
      .put('/ecommerce/order', {
        lines: [...lines],
      })
      .then(res => res.data)
      .catch(err => {
        console.log('unable to update order lines', err);
        throw new Error('Unable to update order');
      });
  };

  updateCoupon = async (coupon: null) => {
    const res = await this.mwApi.patch('/ecommerce/coupon', {coupon});
    return res.data;
  };

  validateOrder = async () => {
    const res = await this.mwApi.post('/ecommerce/order-validate');
    return res.data;
  };

  checkoutOrder = async (data: {
    payment_instrument_id: any;
    browser_information: {
      java_enabled: boolean;
      language: string;
      color_depth: number;
      screen_height: number;
      screen_width: number;
      timezone: number;
    };
  }) => {
    const res = await this.mwApi.post('/ecommerce/order-checkout', {
      order: data,
    });
    return res.data;
  };

  pollOrder = (orderId: any) => {
    return this.mwApi
      .get(`/ecommerce/orders/${orderId}`)
      .then(res => res.data)
      .catch(err => {
        console.log('unable to poll order status');
        throw err;
      });
  };

  getPickupTimeSlots = () => {
    return this.mwApi
      .get('/ecommerce/pickup-slots')
      .then(res => res)
      .catch(err => console.log('unable to load pickup slots for order', err));
  };

  getDeliveryTimeSlots = (regionId: any) => {
    return this.mwApi
      .get('/ecommerce/delivery-slots', {
        params: {regionId},
      })
      .then(res => res)
      .catch(err =>
        console.log('unable to load delivery slots for order', err),
      );
  };

  getRegions = (query: any) => {
    return this.mwApi
      .get('/ecommerce/regions', {params: {query}})
      .then(res => res)
      .catch(() => console.log('unable to get region for address'));
  };

  updateDeliveryDetails = (data: any) => {
    return this.mwApi
      .patch('/ecommerce/delivery-details', data)
      .then(res => res.data)
      .catch(err => console.log('unable to update delivery details', err));
  };

  updateOrderNote = (data: {note: string}) => {
    return this.mwApi
      .patch('/ecommerce/order-note', data)
      .then(res => res)
      .catch(err => {
        console.log('unable to update order note api', err.message);
      });
  };

  updateGiftCardRecipientInformation = async (
    giftCardRecipientInformation: GiftCardRecipientData,
  ) =>
    this.mwApi.patch(
      '/ecommerce/orders/current/gift_information',
      giftCardRecipientInformation,
    );

  getCompetitionsForOrder = (orderId: string) => {
    return this.mwApi
      .get(`/ecommerce/competitions/${orderId}`)
      .then(res => res)
      .catch(err => {
        console.log(
          'unable to load eligible competitions for order',
          err.response?.data?.errors?.map((e: {code: any}) => e.code).join(';'),
        );
        return [];
      });
  };

  // Support
  createSupportTicket = async (ticket: any) =>
    this.mwApi.post('/support/support-ticket', ticket);

  createSelfExclusionRequest = async (request: any) =>
    this.mwApi.post('/support/self-exclusion-request', request);

  createThirdPartyReviewRequest = async (request: any) =>
    this.mwApi.post('/support/third-party-review-request', request);

  // Nexus Catalogues
  getCatalogueHtml = async (storeId: string | null, storeState: any) => {
    const response = this.mwApi.get('/catalogues/catalogueHtml', {
      params: {storeId, storeState},
    });
    return response as unknown as Promise<string>;
  };

  // 3DS
  getBrowserDataJs = async () => this.mwApi.get('/3ds/browserDataJs');

  // Gift Cards
  getGiftCardVoucherData = async (serialNumber: string, pin: string) =>
    this.mwApi.post<EnquireVoucherResponse | EnquireVoucherErrorResponse>(
      '/gift-cards/voucher/gift/enquire',
      {
        serialNumber,
        pin,
      },
    );

  // Information
  getInformation = async (contentType: string) =>
    this.mwApi.get('/content/info', {
      params: {
        contentType,
      },
    });

  getRewardsInformation = async (content: string) =>
    this.mwApi.get<
      any,
      AxiosResponse<RewardsInformationResponse, unknown>,
      any
    >('/content/rewards', {
      params: {content},
    });

  createZenGiftCardPaymentInstrument = async (
    zenGiftCardsPaymentInstrument: CreateZenGiftCardPaymentInstrumentRequest,
  ) => {
    try {
      const res = await this.mwApi.post(
        '/ecommerce/gift-cards/payment-instruments',
        zenGiftCardsPaymentInstrument,
      );
      return res;
    } catch (err: any) {
      console.log(
        'unable to create payment instrument',
        err.response?.status,
        err.response?.data,
      );
      throw new Error(
        'We are having trouble verifying your Gift Card, please try again.',
      );
    }
  };

  updateCurrentOrderTenderOptions = async (request: any) => {
    try {
      const res = await this.mwApi.post(
        '/ecommerce/order-tender-options',
        request,
      );
      return res;
    } catch (err: any) {
      console.log(
        'unable to create payment instrument',
        err.response?.status,
        err.response?.data,
      );
      throw new Error(err.response?.data || 'Unable to store card');
    }
  };

  rewardsOptIn = async (data: RewardsOptInParams) => {
    try {
      return await this.mwApi.post('/rewards/opt-in', data);
    } catch (err) {
      console.error('Rewards opt in error', err);
      return null;
    }
  };

  rewardsOptOut = async (data: RewardsOptOutParams) => {
    try {
      return await this.mwApi.post('/rewards/opt-out', data);
    } catch (err) {
      console.error('Rewards opt out error', err);
      return null;
    }
  };

  rewardsCheckAccountExists = async (data: RewardsCheckAccountExistsParams) => {
    try {
      return await this.mwApi.post('/rewards/check-account-exists', {data});
    } catch (err) {
      console.error('Rewards sign in error', err);
      return null;
    }
  };

  rewardsVerifyNewUserPhoneNumber = async (
    data: RewardsVerifyNewPhoneNumberParams,
  ) => {
    try {
      return await this.mwApi.post('/rewards/send-verification-new-lite-user', {
        data,
      });
    } catch (err) {
      console.error('rewardsVerifyNewUserPhoneNumber error', err);
      return null;
    }
  };

  rewardsVerifyExistingUserPhoneNumber = async (
    data: RewardsVerifyExistingPhoneNumberParams,
  ) => {
    try {
      return await this.mwApi.post('/rewards/send-verification', {
        data: {
          ...data,
          isEmail: false,
        },
      });
    } catch (err) {
      console.error('rewardsVerifyExistingUserPhoneNumber error', err);
      return null;
    }
  };

  rewardsVerifyExistingUserEmail = async (
    data: VerifyExistingUserEmailAddressParams,
  ) => {
    try {
      return await this.mwApi.post('/rewards/send-verification', {
        data: {
          ...data,
          isEmail: true,
        },
      });
    } catch (err) {
      console.error('rewardsVerifyExistingUserEmail error', err);
      return null;
    }
  };

  rewardsSignUp = async (data: RewardsSignUpParams) => {
    try {
      return await this.mwApi.post<RewardsSignUpResponse>(
        '/rewards/verification-lite',
        {data},
      );
    } catch (err) {
      console.error('rewardsSignUp error', err);
      return null;
    }
  };

  rewardsGetAccount = async (data: RewardsGetAccountParams) => {
    try {
      return await this.mwApi.get<RewardsGetAccountResponse>(
        '/rewards/account-details',
        {
          params: {phoneNumber: data.phoneNumber},
        },
      );
    } catch (err) {
      console.error('Rewards get account error', err);
      return null;
    }
  };

  getCommunicationsPreferences = async () => {
    try {
      return await this.mwApi.get('/aggregate/communications-preferences');
    } catch (err) {
      console.error('getCommunicationsPreferences error', err);
      return null;
    }
  };

  updateCommunicationsPreferences = async (
    data: CommunicationsPreferencesParams,
  ) => {
    try {
      return await this.mwApi.post('/aggregate/communications-preferences', {
        data,
      });
    } catch (err) {
      console.error('updateCommunicationsPreferences error', err);
      return null;
    }
  };

  // Tmp: Summer Cash Grab
  getSummerCashGrabHtml = async (iFrameLinkParams: string) => {
    const response = this.mwApi.get(
      `/games/summerCashGrab/?${iFrameLinkParams}`,
    );
    return (await response).data as unknown as Promise<{
      initScript: string;
      gameUri: string;
      htmlContent: string;
    }>;
  };
}

const apiService = new ApiService();
export default apiService;

export enum GetDepartmentLinesSortOptionId {
  TOP_PRODUCTS = 'top_products',
  NAME = 'name',
  NAME_DESCENDING = 'name_descending',
  PRICE = 'price',
  PRICE_DESCENDING = 'price_descending',
  UNIT_PRICE = 'unit_price',
  DISCOUNT_PERCENT = 'discount_percent',
  DISCOUNT_CENTS = 'discount_cents',
}

export type GetDepartmentLinesRequestParams = {
  department_id?: string;
  sort_option_id?: GetDepartmentLinesSortOptionId;
  facets?: string[];
  page_size?: number;
};
